import React from 'react'
import { RichText, type ComponentProps, type ExtendedRichTextData, type PartData } from '@enonic/react-components'
import NewsletterSignupPrototype from '/react4xp/components/prototype/NewsletterSignup'

type NewsletterSignupPartData = {
	// Which of the part's two form sources the editor picked: 'enonic' for the form
	// below, 'superoffice' for a pasted SuperOffice snippet rendered in its place.
	formSource?: string
	superOfficeScript?: string
	submitUrl?: string
	contentId?: string
	componentPath?: string
	title?: string
	description?: string
	fieldLabel?: string
	privacyText?: ExtendedRichTextData
	placeholder?: string
	submitLabel?: string
	requiredMessage?: string
	invalidEmailMessage?: string
	successMessage?: string
	genericErrorMessage?: string
}

/**
 * Runs a snippet pasted by an editor inside `container`.
 *
 * `dangerouslySetInnerHTML` parses `<script>` tags but never executes them, so each one
 * has to be re-created as a real element for the browser to fetch and run it. Attributes
 * are copied over as authored (`src`, `defer`, `async`, ids, data-*), and any inline body
 * is carried across too, so a snippet mixing markup and script behaves as it would on a
 * plain HTML page.
 */
function runSnippet(container: HTMLElement, snippet: string) {
	const template = document.createElement('template')
	template.innerHTML = snippet
	container.appendChild(template.content)

	container.querySelectorAll('script').forEach((original) => {
		const script = document.createElement('script')
		for (const { name, value } of Array.from(original.attributes)) script.setAttribute(name, value)
		script.text = original.text
		original.replaceWith(script)
	})
}

export const NewsletterSignup = ({ component, meta, data, common }: ComponentProps<PartData>) => {
	const {
		formSource, superOfficeScript, submitUrl, contentId, componentPath, title, description, fieldLabel,
		privacyText, placeholder, submitLabel, requiredMessage, invalidEmailMessage, successMessage, genericErrorMessage,
	} = (data || {}) as NewsletterSignupPartData

	const isSuperOffice = formSource === 'superoffice'
	const [sent, setSent] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)

	const superOfficeRef = React.useRef<HTMLDivElement | null>(null)
	const superOfficeRanRef = React.useRef(false)

	React.useEffect(() => {
		if (!isSuperOffice || !superOfficeScript || !superOfficeRef.current || superOfficeRanRef.current) return
		// Once per mount: the snippet's own script keeps state of its own, and running it
		// twice would leave two SuperOffice forms in the container.
		superOfficeRanRef.current = true
		runSnippet(superOfficeRef.current, superOfficeScript)
	}, [isSuperOffice, superOfficeScript])

	const handleSubmit = (values: Record<string, unknown>) => {
		// Sent as urlencoded rather than multipart so the service can read the fields
		// straight off `request.params`.
		const body = new URLSearchParams()
		body.set('email', String(values.email || ''))
		if (contentId) body.set('contentId', contentId)
		if (componentPath) body.set('componentPath', componentPath)

		setError(null)
		fetch(submitUrl!, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
			body: body.toString(),
		})
			.then((response) => {
				if (!response.ok) throw new Error(`Newsletter signup failed: ${response.status}`)
				setSent(true)
			})
			.catch(() => setError(genericErrorMessage || null))
	}

	// SuperOffice mode: the section keeps its heading, description and layout, and
	// formSlot takes the place of our input, button and validation. Unlike ContactForm's
	// prototype, this one renders the privacy paragraph outside formSlot and falls back
	// to hardcoded English copy for an undefined `fieldLabel` / `privacyText`, so both
	// are suppressed explicitly here — React default parameters fire on `undefined` only.
	// The container is left empty on the server — the snippet only runs in the browser.
	if (isSuperOffice) {
		return (
			<NewsletterSignupPrototype
				heading={title}
				description={description}
				fieldLabel={fieldLabel || ''}
				privacyText={null}
				formSlot={<div ref={superOfficeRef} />}
			/>
		)
	}

	const privacyNode = privacyText ? (
		<RichText tag="div" className="rich-text" data={privacyText} component={component} meta={meta} common={common} />
	) : null

	// A part saved before the form source became mandatory emits no `formSource`. Render
	// the copy with an empty form slot (an empty fragment is not nullish, so it does not
	// fall back to the prototype's own form) until an editor re-picks the source.
	if (formSource !== 'enonic') {
		return (
			<NewsletterSignupPrototype
				heading={title}
				description={description}
				fieldLabel={fieldLabel || ''}
				privacyText={privacyNode}
				formSlot={<></>}
			/>
		)
	}

	const errorNode = error ? (
		<p role="alert" className="form-error">
			{error}
		</p>
	) : null

	return (
		<NewsletterSignupPrototype
			heading={title}
			description={description}
			fieldLabel={fieldLabel || ''}
			// The prototype has no sent/error state of its own, so the confirmation
			// replaces the form through formSlot, and a failure is surfaced as a line
			// above the privacy sentence with the form left in place to retry.
			privacyText={
				errorNode || privacyNode ? (
					<>
						{errorNode}
						{privacyNode}
					</>
				) : null
			}
			formSlot={sent ? <p className="body text-black-950">{successMessage}</p> : undefined}
			labels={{ emailPlaceholder: placeholder, submitLabel, requiredMessage, invalidEmailMessage }}
			onSubmit={handleSubmit}
		/>
	)
}
